import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  checkApi,
  type DefectDetail,
  type DefectListFilter,
  type DefectSummary,
  type RecordDefectActionRequest,
} from '../client';

export const defectKeys = {
  all: ['check', 'defects'] as const,
  list: (organisationId: string, filter: DefectListFilter) =>
    ['check', 'defects', 'org', organisationId, filter] as const,
  byId: (id: string) => ['check', 'defects', 'id', id] as const,
};

/**
 * The defect queue — unresolved first, then most severe, then oldest. That
 * ordering is applied server-side so the 200-row cap keeps the most urgent
 * rather than an arbitrary slice.
 */
export function useDefects(
  organisationId: string | null,
  filter: DefectListFilter = {},
  options?: Omit<UseQueryOptions<DefectSummary[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<DefectSummary[], Error>({
    queryKey: organisationId ? defectKeys.list(organisationId, filter) : defectKeys.all,
    queryFn: () => checkApi.getDefects(organisationId!, filter),
    enabled: !!organisationId,
    staleTime: 15 * 1000,
    ...options,
  });
}

/** One defect with its provenance and full action trail. */
export function useDefect(id: string | null, organisationId: string | null) {
  return useQuery<DefectDetail, Error>({
    queryKey: id ? defectKeys.byId(id) : defectKeys.all,
    queryFn: () => checkApi.getDefect(id!, organisationId!),
    enabled: !!id && !!organisationId,
  });
}

/**
 * Append an action.
 *
 * Invalidates both the detail (new trail entry) and the list, because the
 * defect's status may have changed — which reorders the queue and can move the
 * card into or out of an active filter.
 */
export function useRecordDefectAction() {
  const qc = useQueryClient();
  return useMutation<string, Error, RecordDefectActionRequest>({
    mutationFn: (body) => checkApi.recordDefectAction(body),
    onSuccess: (_id, body) => {
      qc.invalidateQueries({ queryKey: defectKeys.byId(body.defectId) });
      qc.invalidateQueries({ queryKey: ['check', 'defects', 'org', body.organisationId] });
    },
  });
}
